import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {PwaPlatform} from './pwa-platform.service';

export interface PwaInstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{outcome: string}>;
}
export const PWA_INSTALL_ROUTE = '/install';
const BEFORE_INSTALL = 'beforeinstallprompt';
const INSTALLED_EVENT = 'appinstalled';
const NO_ERROR = '';
const INSTALL_FAILED = 'PWA_INSTALL_FAILED';

@Injectable()
export class PwaInstallService implements OnDestroy {
  errorKey = NO_ERROR;
  private deferred: PwaInstallPrompt;
  private installed = false;
  private pending = false;
  private removeListeners: (() => void)[];

  constructor(private platform: PwaPlatform, private router: Router, zone: NgZone) {
    this.removeListeners = [
      platform.listen(BEFORE_INSTALL, event => zone.run(() => this.capture(event as PwaInstallPrompt))),
      platform.listen(INSTALLED_EVENT, () => zone.run(() => { this.installed = true; this.deferred = null; }))
    ];
  }

  get isInstalled(): boolean { return this.installed || this.platform.isStandalone; }
  get canInstall(): boolean { return !!this.deferred && !this.isInstalled && !this.pending; }
  get isIos(): boolean { return this.platform.isIos; }

  install(): Promise<void> {
    if (this.isInstalled || this.pending) { return Promise.resolve(); }
    this.errorKey = NO_ERROR;
    if (!this.deferred) { return this.openHelp(); }
    const prompt = this.deferred;
    this.deferred = null;
    this.pending = true;
    return this.showPrompt(prompt).then(() => prompt.userChoice).then(() => { this.pending = false; }, () => {
      this.pending = false;
      return this.openHelp();
    });
  }

  ngOnDestroy(): void { this.removeListeners.forEach(remove => remove()); }

  private capture(event: PwaInstallPrompt): void {
    event.preventDefault();
    this.deferred = event;
  }

  private showPrompt(prompt: PwaInstallPrompt): Promise<void> {
    try { return prompt.prompt(); } catch (error) { return Promise.reject(error); }
  }

  private openHelp(): Promise<void> {
    return this.router.navigateByUrl(PWA_INSTALL_ROUTE).then(success => {
      if (!success) { this.errorKey = INSTALL_FAILED; }
    }, () => { this.errorKey = INSTALL_FAILED; });
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/pwa-install.service.ts